// The library in memory, backed by IndexedDB. Local edits bump the group's timestamp and mark the article dirty for
// sync (SYNC.md "Client algorithm").
import * as db from "../store/db";
import { Hub, nowMs } from "../util";
import type { Article } from "./article";

class Library {
  articles = new Map<string, Article>();
  loaded: Promise<void>;
  readonly events = new Hub<{ changed: string | null; deleted: string }>();
  /** Set by sync: an article changed locally (meta and/or progress). */
  onLocalChange: (id: string, meta: boolean, progress: boolean) => void = () => {};
  onLocalDelete: (id: string) => void = () => {};

  constructor() { this.loaded = this.load(); }

  private async load() {
    try { for (const [id, a] of await db.all<Article>("articles")) this.articles.set(id, a); } catch (e) { console.warn("library load", e); }
    this.events.emit("changed", null);
  }

  get(id: string): Article | undefined { return this.articles.get(id); }
  list(): Article[] { return [...this.articles.values()]; }

  /** Stores an article as is (sync applies remote data this way; never bumps timestamps). */
  store(a: Article): void {
    this.articles.set(a.id, a);
    db.put("articles", a.id, a).catch((e) => console.warn("save article", e));
    this.events.emit("changed", a.id);
  }

  /** A local edit of the synced meta. */
  editMeta(id: string, edit: (m: Article["meta"]) => void): void {
    const a = this.articles.get(id);
    if (!a) return;
    const copy: Article = { ...a, meta: { ...a.meta }, metaUpdatedAt: nowMs() };
    edit(copy.meta);
    this.store(copy);
    this.onLocalChange(id, true, false);
  }

  /** A local change of listening progress. */
  setProgress(id: string, chunk: number, offset: number, finished = false): void {
    const a = this.articles.get(id);
    if (!a) return;
    if (a.progress.chunk === chunk && Math.abs(a.progress.offset - offset) < 0.05 && a.progress.finished === finished) return;
    this.store({ ...a, progress: { chunk, offset, finished }, progressUpdatedAt: nowMs() });
    this.onLocalChange(id, false, true);
  }

  markOpened(id: string): void {
    const a = this.articles.get(id);
    if (a) this.store({ ...a, openedAt: nowMs() });
  }

  /** Deleted here: a tombstone goes to the database. */
  delete(id: string): void {
    this.removeLocal(id);
    this.onLocalDelete(id);
  }

  /** Deleted (here or by sync): forget the article and its audio. */
  removeLocal(id: string): void {
    this.articles.delete(id);
    db.del("articles", id).catch(() => {});
    db.delPrefix("audio", id + ":").catch(() => {});
    this.events.emit("deleted", id);
    this.events.emit("changed", id);
  }
}

export const library = new Library();
